import React from "react";
import { Trash2 } from "lucide-react";
import { defaultPostback, emptySuggestion } from "./templatePayload";

export const SUGGESTION_KINDS = [
  { value: "reply", label: "Quick reply" },
  { value: "openUrl", label: "Open URL" },
  { value: "dial", label: "Dial number" },
  { value: "calendar", label: "Calendar event" },
];

export const inputClass = (error) =>
  `w-full px-3 py-2 border rounded-lg text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 ${
    error
      ? "border-red-300 focus:ring-red-100 focus:border-red-400"
      : "border-gray-200 focus:ring-[#00b6ce]/20 focus:border-[#00b6ce]"
  }`;

export const FieldError = ({ error }) =>
  error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null;

const SmallField = ({ label, value, onChange, error, placeholder, maxLength }) => (
  <label className="block">
    <span className="block text-xs font-medium text-gray-500 mb-1">{label}</span>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      className={inputClass(error)}
    />
    <FieldError error={error} />
  </label>
);

const SuggestionEditor = ({ suggestions, onChange, errors, prefix }) => {
  const listError = errors[`${prefix}.suggestions`];

  const update = (index, patch) =>
    onChange(suggestions.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  const remove = (index) => onChange(suggestions.filter((_, i) => i !== index));
  const add = (kind) => onChange([...suggestions, emptySuggestion(kind)]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-700">Buttons</span>
        <select
          value=""
          onChange={(e) => e.target.value && add(e.target.value)}
          className="px-2 py-1 border border-gray-200 rounded-lg text-xs text-[#00b6ce] font-semibold bg-white"
          aria-label="Add button"
        >
          <option value="">+ Add button</option>
          {SUGGESTION_KINDS.map((k) => (
            <option key={k.value} value={k.value}>
              {k.label}
            </option>
          ))}
        </select>
      </div>
      <FieldError error={listError} />

      {suggestions.map((s, i) => {
        const key = `${prefix}.suggestions.${i}`;
        const err = (f) => errors[`${key}.${f}`];
        return (
          <div key={i} className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-2">
            <div className="flex items-center gap-2">
              <select
                value={s.kind}
                onChange={(e) => update(i, { kind: e.target.value })}
                className="px-2 py-1 border border-gray-200 rounded-lg text-xs bg-white text-gray-700"
                aria-label="Button type"
              >
                {SUGGESTION_KINDS.map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </select>
              <span className="text-xs text-gray-400">Button {i + 1}</span>
              <button
                type="button"
                onClick={() => remove(i)}
                className="ml-auto text-gray-400 hover:text-red-500 p-1"
                title="Remove button"
              >
                <Trash2 size={14} />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <SmallField
                label={`Text (${s.text.length}/25)`}
                value={s.text}
                onChange={(v) => update(i, { text: v })}
                error={err("text")}
              />
              <SmallField
                label="Postback data"
                value={s.postback}
                onChange={(v) => update(i, { postback: v })}
                placeholder={defaultPostback(s.text, i)}
                error={err("postback")}
              />
              {s.kind === "openUrl" && (
                <SmallField
                  label="URL"
                  value={s.url}
                  onChange={(v) => update(i, { url: v })}
                  placeholder="https://example.com"
                  error={err("url")}
                />
              )}
              {s.kind === "dial" && (
                <SmallField
                  label="Phone number"
                  value={s.phone}
                  onChange={(v) => update(i, { phone: v })}
                  placeholder="+919876543210"
                  error={err("phone")}
                />
              )}
              {s.kind === "calendar" && (
                <>
                  <SmallField
                    label="Start time"
                    value={s.startTime}
                    onChange={(v) => update(i, { startTime: v })}
                    placeholder="{{1}}"
                    error={err("startTime")}
                  />
                  <SmallField
                    label="End time"
                    value={s.endTime}
                    onChange={(v) => update(i, { endTime: v })}
                    placeholder="{{2}}"
                    error={err("endTime")}
                  />
                  <SmallField
                    label="Event title"
                    value={s.title}
                    onChange={(v) => update(i, { title: v })}
                    error={err("title")}
                  />
                  <SmallField
                    label="Event description"
                    value={s.description}
                    onChange={(v) => update(i, { description: v })}
                    error={err("description")}
                  />
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SuggestionEditor;
